// Core
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'

// Library
import clsx from 'clsx'

// Components
import StepProgress from 'components/Onboarding/StepProgress/StepProgress'
import BlockQuote from 'components/Onboarding/BlockQuote/BlockQuote'
import BulletList from 'components/Onboarding/BulletList/BulletList'
import Button from 'components/Onboarding/Button/Button'

// Assets
import { ReactComponent as Logo } from 'assets/icons/logo_clicktech.svg'
import { ReactComponent as IconDD } from 'assets/icons/logo_dd.svg'
import { ReactComponent as BusinessIcon } from 'assets/icons/onboarding/business.svg'
import { ReactComponent as FreelancerIcon } from 'assets/icons/onboarding/freelancer.svg'
import { ReactComponent as AgencyIcon } from 'assets/icons/onboarding/agency.svg'
import { ReactComponent as EnterpriseIcon } from 'assets/icons/onboarding/enterprise.svg'

// Styles
import classes from 'pages/Onboarding/Onboarding.module.scss'
import ButtonOptions from 'components/Onboarding/ButtonOptions/ButtonOptions'

// Constants
import { BUSINESS_TYPE_VALUES } from 'pages/Onboarding/Profile/Business/constants'

// Routes
import routes from 'routes'

// Utils
import {sendGTMEvent} from 'utils/gtm/sendGTMEvent'

const ProfileBusiness = () => {
  const history = useHistory()
  const [selectedOptions, setSelectedOptions] = useState([] as string[])
  const options = [
    {
      value: BUSINESS_TYPE_VALUES.BUSINESS,
      icon: <BusinessIcon />,
      title: 'Business',
      description: 'I run digital marketing for a company',
    },
    {
      value: BUSINESS_TYPE_VALUES.FREELANCER,
      icon: <FreelancerIcon />,
      title: 'Freelancer',
      description: 'I manage campaigns independently',
    },
    {
      value: BUSINESS_TYPE_VALUES.AGENCY,
      icon: <AgencyIcon />,
      title: 'Agency',
      description: 'We handle multiple client accounts',
    },
    {
      value: BUSINESS_TYPE_VALUES.ENTERPRISE,
      icon: <EnterpriseIcon />,
      title: 'Enterprise',
      description: 'We manage ad distribution at scale',
    }
  ]

  const handleContinue = () => {
    localStorage.setItem('onboarding', JSON.stringify({
      business_type: selectedOptions[0]
    }))

    sendGTMEvent({
      event: 'businessTypeComplete',
      business_type: selectedOptions[0]
    })

    history.push(routes.onboardingProfilePriorities)
  }
  
  return (
    <div className={clsx(classes.stepContainer)}>
      <div className={clsx(classes.stepAction)}>
        <div className={clsx(classes.stepSection, 'mt-2', 'mt-xl-up-5')}>
          <div className={clsx(classes.logo)}>
            <Logo />
          </div>
        </div>
        <div className={clsx(classes.stepSection)}>
          <div className={clsx(classes.stepProgress)}>
            <StepProgress step={1} label="account" theme="complete" />
            <StepProgress step={2} label="profile" theme="active" />
            <StepProgress step={3} label="usage" theme="pending" className="d-none-sm" />
            <StepProgress step={4} label="connect" theme="pending" className="d-none-sm" />
          </div>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2')}>
          <h1>Tell us about yourself</h1>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <p>This helps us tailor EDEE to your specific needs.</p>
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <ButtonOptions
            title={<p className="mb-3"><strong>What best describes your business type?</strong></p>}
            options={options}
            selectedOptions={selectedOptions}
            onClick={(value: string) => {
                const existingIndex = selectedOptions.indexOf(value)
                const newSelectedOptions = [ ...selectedOptions ]
                if (existingIndex > -1) {
                  newSelectedOptions.splice(existingIndex, 1)
                } else {
                  newSelectedOptions.splice(0, 1)
                  newSelectedOptions.push(value)
                }
                setSelectedOptions(newSelectedOptions)
            }}
          />
        </div>
        <div className={clsx(classes.stepSection, 'mt-2 mb-4')}>
          <div className="d-flex justify-content-center">
            <Button
              dataCy="continue"
              title="Continue"
              disabled={selectedOptions.length === 0}
              onClick={handleContinue}
            />
          </div>
        </div>
      </div>
      <div className={clsx(classes.infoContainer)}>
        <div className={clsx(classes.infoSection)}>
          <h2 className="fw-semibold">
            EDEE works the way you need it to
          </h2>
        </div>
        <div className={clsx(classes.infoSection, 'mt-2')}>
          <BulletList
            features={[
              'Create custom budget cycles',
              'One-off or repeating budget cycles',
              'Set up multiple budgets per ad account',
              'Track rollover amounts',
              '& more!',
            ]} 
          />
        </div>
        <div className={clsx(classes.infoSection, 'mt-8')}>
          <BlockQuote
            srcImage={<IconDD />}
            quote="EDEE is by far the best tool I've used for budget management. One-stop budget pacing is quick and easy, and I love the features to avoid overspend. Checking budgets for all my accounts now takes maybe 5 minutes, and the automated system to pause campaigns if we reach budgets early lets me sleep better at night."
            author="Taru K, PPC Specialist"
          />
        </div>
      </div>
    </div>
  )
}

export default ProfileBusiness
